<template>
  <drag-resize
    type="nameManager"
    :drag-box-head-style="dragBoxHeadStyle"
    :drag-box-style="dragBoxStyle"
    @resizeTable="dragResizeHeight = $event"
  >
    <template #header>
      <span>{{ $t("nameManager.title") }}</span>
      <i class="icon-close" @click="closeModal"></i>
    </template>
    <template #body>
      <div ref="container" class="name-manager">
        <div class="name-manager__actions">
          <div class="name-manager__actions-add" tabindex="0" @click="addName">
            <span>{{ $t("nameManager.new") }}</span>
            <i class="icon-plus"></i>
          </div>
          <div class="name-manager__actions-edit-delete" tabindex="0">
            <i class="icon-edit-3 name-manager__edit" :class="{ disabled: !selectedItem }" @click="editName"></i>
            <i
              class="icon-trash-2 name-manager__delete"
              :class="{ disabled: !selectedItem && !allSelected }"
              @click="deleteName"
            ></i>
          </div>
        </div>
        <div class="name-manager__table">
          <customtable
            :headers="headers"
            :head-style="headStyle"
            :data="filteredAndSortedItems"
            :parent-scope="parentScope"
            :td-bg="bgOnHover"
            @passItem="selectItem"
            @selectAll="selectAll"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="name-manager__input">
        <input type="text" />
        <label>Refers To</label>
      </div>
    </template>
  </drag-resize>
</template>

<script>
import DesignerMixin from "../DesignerMixin";
import { removePartUntilSymbol, doRangesOverlap, isRange, isCell } from "../../../utils/util";
import { HotkeyScopes, ScopeFilterEnum, NamedExpressionsType } from "../../../utils/constants";
import { rangeLabelToIndex, rangeIndexToLabel, sortRange, getCellId } from "../cell/Utils";
import Customtable from "../../commons/Customtable.vue";
import DragResize from "../../commons/DragResize.vue";

export default {
  components: {
    Customtable,
    DragResize,
  },
  mixins: [DesignerMixin],
  data() {
    return {
      currentFocusIndex: 0,
      parentScope: HotkeyScopes.NameManger,
      showCheckbox: false,
      filterTypes: [
        ScopeFilterEnum.ALL,
        ScopeFilterEnum.WORKBOOK,
        ScopeFilterEnum.WORKSHEET,
        ScopeFilterEnum.WITH_ERRORS,
        ScopeFilterEnum.WITHOUT_ERRORS,
        ScopeFilterEnum.DEFINED_NAMES,
        ScopeFilterEnum.TABLE_NAMES,
      ],
      items: [],
      allSelected: false,
      currentSort: "name",
      currentSortDir: "asc",
      selectedItem: null,
      filterText: "",
      scope: ScopeFilterEnum.ALL,
      previousHotkeyScope: null,
      headers: [
        { label: "", accessor: "checkbox" }, // Checkbox column
        { label: this.$t("goTo.gotocell.type"), accessor: "type", icon: "icon-menu-down" },
        { label: this.$t("goTo.gotocell.name"), accessor: "name", icon: "icon-menu-down" },
        { label: this.$t("goTo.gotocell.scope"), accessor: "scope", icon: "icon-menu-down" },
        { label: this.$t("goTo.gotocell.referTo"), accessor: "refer", icon: "icon-menu-down" },
      ],
    };
  },
  computed: {
    dragBoxHeadStyle() {
      return { marginBottom: "12px" };
    },
    dragBoxStyle() {
      return { backgroundColor: "#fdfeff" };
    },
    bgOnHover() {
      return { backgroundColor: "#d3e3f9" };
    },
    headStyle() {
      return {
        backgroundColor: "#fdfeff",
      };
    },
    filteredAndSortedItems() {
      const { scope, filterText } = this;
      const lowerCaseFilterText = filterText.toLowerCase();

      return this.sortedItems
        .filter((item) => {
          switch (scope) {
            case ScopeFilterEnum.WORKBOOK:
              return item.scope === "Workbook";
            case ScopeFilterEnum.WORKSHEET:
              return item.scope !== "Workbook";
            case ScopeFilterEnum.WITH_ERRORS:
              return item.expression.includes("#REF!");
            case ScopeFilterEnum.WITHOUT_ERRORS:
              return !item.expression.includes("#REF!");
            case ScopeFilterEnum.DEFINED_NAMES:
              return item.type === NamedExpressionsType.NAMEDRANGE;
            case ScopeFilterEnum.TABLE_NAMES:
              return item.type === NamedExpressionsType.TABLE;
            default:
              return true;
          }
        })
        .filter((item) => {
          const searchRange = removePartUntilSymbol(lowerCaseFilterText, "!").replace(/\$/g, "");
          let rangesOverlap = false;

          if (isRange(searchRange)) {
            const itemRange = removePartUntilSymbol(item.expression, "!").replace(/\$/g, "");
            const itemRangeIndices =
              itemRange && (isRange(itemRange) || isCell(itemRange)) ? rangeLabelToIndex(itemRange) : null;
            const searchRangeIndices = searchRange ? rangeLabelToIndex(searchRange) : null;

            rangesOverlap =
              itemRangeIndices && searchRangeIndices && doRangesOverlap(itemRangeIndices, searchRangeIndices);
          }

          return (
            item.name.toLowerCase().includes(lowerCaseFilterText) ||
            item.value?.toString().toLowerCase().includes(lowerCaseFilterText) ||
            item.expression?.toLowerCase().includes(lowerCaseFilterText) ||
            item.scope?.toLowerCase().includes(lowerCaseFilterText) ||
            item.comment?.toLowerCase().includes(lowerCaseFilterText) ||
            rangesOverlap
          );
        })

        .map((item) => {
          let icon;
          if (item.type === "namedRange") {
            icon = "NameRange";
          } else if (item.type === "table") {
            icon = "Table";
          }

          return {
            type: { value: item.type, icon: icon },
            name: { value: item.name },
            selected: item.selected,
            scope: { value: item.scope },
            refer: { value: item.expression },
          };
        });
    },
    sortedItems() {
      return [...this.items].sort((a, b) => {
        let modifier = this.currentSortDir === "asc" ? 1 : -1;
        if (a[this.currentSort] < b[this.currentSort]) return -1 * modifier;
        if (a[this.currentSort] > b[this.currentSort]) return 1 * modifier;
        return 0;
      });
    },
    list() {
      const list = this.$store.getters["table/getAllTableAndNamedRanges"]();
      let tempList = [];
      list.forEach((element) => {
        const scopeSheet = this.allSheets.find((item) => `${item.sheetId}` === `${element.scope}`) ?? "Workbook";
        const scope = scopeSheet.name || "Workbook";
        const sheet = this.allSheets.find((item) => item.id === element.sheetId);
        const expression = `=${sheet.name}!${element.expression}`;
        const selected = false;
        const name = this.$store.getters["table/getLocalNameRangeByDefault"](element.name);
        tempList.push({ ...element, expression, scope, name, selected });
      });
      return tempList;
    },
    isTable() {
      return this.selectedItem?.type === NamedExpressionsType.TABLE;
    },
  },
  watch: {
    filteredAndSortedItems(newFilteredItems) {
      if (!newFilteredItems.some((item) => item.name === this.selectedItem?.name)) {
        this.selectedItem = null;
      }
    },
  },
  mounted() {
    this.items = this.list;
    const rangeValue = this.$store.getters["cells/activeRange"]().range;
    this.filterText = isCell(rangeIndexToLabel(rangeValue)) ? "" : rangeIndexToLabel(rangeValue);
  },

  methods: {
    selectAll() {
      this.allSelected = this.sortedItems.every((item) => item.selected);
      this.items = this.sortedItems.map((item) => ({
        ...item,
        selected: !this.allSelected,
      }));
      this.allSelected = !this.allSelected;
    },

    handleFocus(index) {
      const focusable = this.getFocusableElements();
      if (index < 0) {
        index = focusable.length - 1; // Wrap to the last element if out of bounds
      } else if (index >= focusable.length) {
        index = 0; // Wrap to the first element
      }
      this.currentFocusIndex = index;
      focusable[this.currentFocusIndex]?.focus();
    },

    handleTabNavigation(isShift = false) {
      const focusable = this.getFocusableElements();
      const currentIndex = focusable.indexOf(document.activeElement); // Get current index

      let nextIndex = isShift ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0) nextIndex = focusable.length - 1; // Wrap to last element
      if (nextIndex >= focusable.length) nextIndex = 0; // Wrap to first element

      this.currentFocusIndex = nextIndex;
      this.handleFocus(this.currentFocusIndex);
    },

    getFocusableElements() {
      const container = this.$refs.container;
      if (!container) return []; // Avoid errors if not available yet
      return [...container.querySelectorAll('input:not([type="checkbox"]), div[tabindex="0"]')].filter(
        (el) => !el.disabled && !el.hasAttribute("disabled")
      );
    },
    navigateItems(direction) {
      const items = this.filteredAndSortedItems;
      let index = items.findIndex((item) => item.name === this.selectedItem?.name);
      if (direction === "up" && index > 0) {
        this.selectItem(items[index - 1]);
      } else if (direction === "down" && index < items.length - 1) {
        this.selectItem(items[index + 1]);
      }
    },
    clearFilter() {
      this.filterText = "";
      this.scope = "All";
    },
    selectItem(item) {
      if (item.selected) {
        this.selectedItem = null;
        item.selected = false;
      } else {
        this.selectedItem = item;
        item.selected = true;
      }
    },
    sort(column) {
      if (this.currentSort === column) {
        this.currentSortDir = this.currentSortDir === "asc" ? "desc" : "asc";
      } else {
        this.currentSort = column;
        this.currentSortDir = "asc";
      }
    },
    closeModal() {
      this.$store.dispatch("table/setNameManagerVisibility", false);
    },
    addName() {
      this.closeModal();
      this.$store.dispatch("table/setAddNamedRangeVisibility", true);
      this.$store.dispatch("table/setAddNameSource", "nameManager");
    },
    editName() {
      this.closeModal();
      this.$store.dispatch("cells/selectFormulaRange", this.selectedItem.refer?.value);

      if (this.selectedItem && this.selectedItem.type.value === NamedExpressionsType.NAMEDRANGE) {
        this.$store.dispatch("table/setAddNamedRangeVisibility", true);
        this.$store.dispatch("table/setAddNameSource", "nameManager");
        this.$store.dispatch("table/setEditingNameRange", this.selectedItem);
      } else {
        this.$store.dispatch("table/setActiveTableByName", this.selectedItem.name.value);
        this.$store.dispatch("table/setShowAddTableDialog", true);
      }
    },
    async deleteName() {
      const selectedNames = this.filteredAndSortedItems.filter((item) => item.selected);
      const deletedNamesSet = new Set();

      for (const item of selectedNames) {
        const nameKey = item.name.value;
        deletedNamesSet.add(nameKey);

        if (item.type.value === NamedExpressionsType.NAMEDRANGE) {
          const name = this.$store.getters["table/getDefaultNameRangeByLocal"](nameKey);
          const scope = this.allSheets.find((element) => element.name === item.scope.value)?.sheetId;
          await this.$store.dispatch("table/deleteNamedRanges", { ...item, name, scope });
          this.$store.dispatch("table/persistNamedExpressions");
        } else {
          const tables = this.$store.state.table.tables;
          const table = tables.find((table) => table.tableName.toLowerCase() === nameKey.toLowerCase());
          if (table) {
            this.$store.dispatch("table/deleteTableWithData", table._id);
          }
        }
      }

      this.items = this.items.filter((item) => !deletedNamesSet.has(item.name));
    },
    convertToNameRange() {
      const tables = this.$store.state.table.tables;
      const table = tables.find((table) => table.tableName.toLowerCase() === this.selectedItem.name.toLowerCase());
      this.$store.dispatch("table/convertToRange", table);
      this.selectedItem = null;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables.less";
.name-manager__table {
  border: 1px solid rgba(8, 50, 119, 0.15);
  border-radius: 5px;
  flex: 1;
  overflow-x: auto; // Add this for horizontal scroll
  overflow-y: auto;
}

.table {
  width: 100%;
  min-width: 600px; /* or whatever fits your columns */
  table-layout: auto; // or fixed if using fixed column widths
}

.icon-close {
  cursor: pointer;
}

.name-manager {
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: @kk-color-primary-blue;
  height: 100%;

  &__title {
    i {
      cursor: pointer;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &-add,
    &-edit-delete {
      width: 4.875rem;
      height: 25px;
      border: 1px solid #d8dfeb;
      border-radius: 3px;
      padding: 0px 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &-add:hover {
      background-color: #d3e3f9;
    }

    &-edit-delete {
      .name-manager__edit:hover,
      .name-manager__delete:hover {
        color: #bcc7da;
      }
    }
  }

  &__table {
    border: 1px solid rgba(8, 50, 119, 0.15);
    border-radius: 5px;
    flex: 1;
    overflow-y: auto;
  }

  &__input {
    position: relative;
    border-radius: 3px;
    padding: 6px 10px;
    border: 1px solid rgba(8, 50, 119, 0.13);

    &:focus-within {
      border: 1px solid @kk-color-royal-blue;
    }

    input {
      width: 100%;
      color: @kk-color-primary-blue;

      &:focus {
        border: none;
        outline: none;
      }
    }

    label {
      position: absolute;
      top: -10px;
      left: 8px;
      font-size: 12px;
      color: @kk-color-royal-blue;
      padding: 0 5px;
      background-color: #fdfeff;
    }
  }
}

/* Utility Classes */
.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.name-manager__close {
  cursor: pointer;
}

.name-manager__edit,
.name-manager__delete {
  cursor: pointer;
}
</style>
